import React, { useCallback, useEffect, useState } from 'react';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import { RefreshCw, ChevronLeft, ChevronRight, Download, CreditCard, CheckCircle2, XCircle, Clock } from 'lucide-react';

const PROVIDERS = [
  { value: '', label: 'Tous' },
  { value: 'paypal', label: 'PayPal' },
];
const STATUSES = [
  { value: '', label: 'Tous' },
  { value: 'paid', label: 'Payé' },
  { value: 'pending', label: 'En attente' },
  { value: 'failed', label: 'Échec' },
];
const PAGE_SIZE = 25;

function StatusPill({ status }) {
  const s = (status || '').toLowerCase();
  const map = {
    paid: { label: 'Payé', color: '#18794e', bg: '#f0fdf4', Icon: CheckCircle2 },
    pending: { label: 'En attente', color: '#f59e0b', bg: '#fef3c7', Icon: Clock },
    failed: { label: 'Échec', color: '#b42318', bg: '#fef2f2', Icon: XCircle },
  };
  const conf = map[s] || { label: s || '—', color: '#64748b', bg: '#f1f5f9', Icon: Clock };
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest rounded-full px-2 py-1" style={{ background: conf.bg, color: conf.color }}>
      <conf.Icon className="w-3 h-3" /> {conf.label}
    </span>
  );
}

function ProviderBadge({ provider }) {
  const p = (provider || 'stripe').toLowerCase();
  if (p === 'paypal') {
    return <span className="text-[10px] font-black uppercase tracking-widest rounded-full px-2 py-0.5" style={{ background: '#003087', color: '#ffc439' }}>PayPal</span>;
  }
  return <span className="text-[10px] font-black uppercase tracking-widest rounded-full px-2 py-0.5" style={{ background: '#635bff15', color: '#635bff' }}>Stripe</span>;
}

export default function AdminPayments() {
  const [data, setData] = useState({ total: 0, transactions: [] });
  const [loading, setLoading] = useState(true);
  const [provider, setProvider] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(0);

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getAdminPayments({
        provider: provider || undefined,
        status: status || undefined,
        skip: page * PAGE_SIZE,
        limit: PAGE_SIZE,
      });
      setData(res);
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally { setLoading(false); }
  }, [provider, status, page]);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  const totalPages = Math.ceil((data.total || 0) / PAGE_SIZE);
  const paidCount = data.transactions.filter(t => t.payment_status === 'paid').length;
  const paidAmount = data.transactions.filter(t => t.payment_status === 'paid').reduce((s, t) => s + (t.amount || 0), 0);

  const handleExportCSV = () => {
    const rows = [['Date', 'Provider', 'Email', 'Amount', 'Currency', 'Status', 'Session ID']];
    data.transactions.forEach(t => {
      rows.push([
        t.created_at || '',
        t.provider || 'stripe',
        t.user_email || '',
        t.amount || '',
        t.currency || '',
        t.payment_status || '',
        t.session_id || '',
      ]);
    });
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payments_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5" data-testid="admin-payments-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-black">Paiements</h1>
          <p className="text-sm text-muted-foreground mt-1">Historique Stripe + PayPal · {data.total} transactions au total</p>
        </div>
        <Button onClick={handleExportCSV} variant="outline" className="rounded-full" data-testid="export-payments-csv">
          <Download className="w-4 h-4 mr-1.5" /> Export CSV (page)
        </Button>
      </div>

      {/* Stats rapides */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="mc-card p-4" data-testid="stat-total">
          <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Total page</div>
          <div className="text-2xl font-black mt-1">{data.transactions.length}</div>
        </div>
        <div className="mc-card p-4" data-testid="stat-paid-count">
          <div className="text-[10px] font-black uppercase tracking-widest text-[#18794e]">Payés (page)</div>
          <div className="text-2xl font-black mt-1 text-[#18794e]">{paidCount}</div>
        </div>
        <div className="mc-card p-4" data-testid="stat-paid-amount">
          <div className="text-[10px] font-black uppercase tracking-widest text-[hsl(var(--primary))]">Revenu (page)</div>
          <div className="text-2xl font-black mt-1 text-[hsl(var(--primary))]">{paidAmount.toFixed(2)} €</div>
        </div>
        <div className="mc-card p-4" data-testid="stat-all">
          <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Total base</div>
          <div className="text-2xl font-black mt-1">{data.total}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="mc-card p-4 flex items-center gap-3 flex-wrap">
        <select
          value={provider}
          onChange={(e) => { setProvider(e.target.value); setPage(0); }}
          className="px-3 py-2 rounded-lg border border-border bg-background text-sm"
          data-testid="filter-provider"
        >
          {PROVIDERS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        <select
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(0); }}
          className="px-3 py-2 rounded-lg border border-border bg-background text-sm"
          data-testid="filter-status"
        >
          {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <Button onClick={fetchPayments} variant="outline" size="sm" className="rounded-full ml-auto" data-testid="refresh-btn">
          <RefreshCw className={cn('w-3.5 h-3.5', loading && 'animate-spin')} />
        </Button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="mc-card p-10 text-center">
          <RefreshCw className="w-6 h-6 animate-spin mx-auto text-[hsl(var(--primary))] mb-2" />
          <p className="text-sm text-muted-foreground">Chargement…</p>
        </div>
      ) : data.transactions.length === 0 ? (
        <div className="mc-card p-10 text-center text-sm text-muted-foreground" data-testid="empty">
          Aucune transaction ne correspond aux filtres.
        </div>
      ) : (
        <div className="mc-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr className="text-left text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Provider</th>
                  <th className="px-4 py-3">Utilisateur</th>
                  <th className="px-4 py-3 text-right">Montant</th>
                  <th className="px-4 py-3">Statut</th>
                  <th className="px-4 py-3">Session / Order</th>
                </tr>
              </thead>
              <tbody>
                {data.transactions.map((t, i) => {
                  const d = t.created_at ? new Date(t.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
                  return (
                    <tr key={t.id || t.session_id || i} className="border-t border-border hover:bg-muted/30 transition-colors" data-testid={`tx-row-${i}`}>
                      <td className="px-4 py-3 text-xs tabular-nums whitespace-nowrap">{d}</td>
                      <td className="px-4 py-3"><ProviderBadge provider={t.provider} /></td>
                      <td className="px-4 py-3 font-semibold">{t.user_email}</td>
                      <td className="px-4 py-3 text-right font-black tabular-nums">{(t.amount || 0).toFixed(2)} {(t.currency || 'eur').toUpperCase()}</td>
                      <td className="px-4 py-3"><StatusPill status={t.payment_status} /></td>
                      <td className="px-4 py-3 text-[10px] font-mono text-muted-foreground truncate max-w-[180px]">{t.session_id}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2" data-testid="pagination">
          <Button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} variant="outline" size="sm">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm font-bold">Page {page + 1} / {totalPages}</span>
          <Button onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1} variant="outline" size="sm">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
